/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable react/no-danger */
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { WarningFilled } from "@duxton-web/icons"
import React from "react"
import { Button, Drawer } from "@duxton-web/ui"
import { useTranslationsMap } from "common/utils/i18n"
import { ReviewThrottleResponse } from "store/duck/feedback.type"
import { ReviewUnReportableReason } from "types/api/menuFeedback.type"
import dayjs from "dayjs"
import { drawerFooter } from "components/drawers/styles"
import useFEConfig from "hooks/useFEConfig"
import { REPORT_POLICY_LINK } from "constants/links"
import DOMPurify from "dompurify"
import { replaceRegexIntoLink, replaceRegexIntoLinkType } from "utils/i18n"
import { isUserCountryUse24hFormat } from "utils/time_helper"
import { getRangeTimeFormat } from "order-manager/utils/time_helper"
import styles from "./styles.modules.css"

type FeedbackReportThrottleProps = {
  onClose(): void
  reviewThrottle: ReviewThrottleResponse
  visible?: boolean
}

const FeedbackReportThrottle: React.FC<FeedbackReportThrottleProps> = ({
  onClose,
  reviewThrottle,
  visible,
}) => {
  const feConfig = useFEConfig()
  const textMap = useTranslationsMap({
    report: "feedback.titleBar.report",
    reachedLimitReportingTitle: "reviewThrottle.reachedLimitReportingTitle",
    reachedLimitReportingBody: "reviewThrottle.reachedLimitReportingBody",
    blockedReportingTitle: "reviewThrottle.blockedReportingTitle",
    reachedLimitReportingBodyDetail:
      "reviewThrottle.reachedLimitReportingBodyDetail",
    blockedReportingBody: "reviewThrottle.blockedReportingBody",
    blockedReportingBodyFooter: "reviewThrottle.blockedReportingBodyFooter",
    blockedReportingBodyMiddle: "reviewThrottle.blockedReportingBodyMiddle",
    disabledReportingTitle: "reviewThrottle.disabledReportingTitle",
    disabledReportingBody: "reviewThrottle.disabledReportingBody",
    buttonConfirm: "reviewThrottle.buttonConfirm",
  })

  let limitReachTitle
  let limitReachBodyUpper
  let limitReachBodyMiddle
  let limitReachBodyFooter

  const rangeTimeFormat = getRangeTimeFormat(isUserCountryUse24hFormat()).Right

  const reachedLimitReportingBodyDetail = replaceRegexIntoLink(
    textMap.reachedLimitReportingBodyDetail.replace(
      "{date}",
      dayjs(reviewThrottle.reviewsReportingFreezeEndDate).format(
        `DD MMM YYYY ${rangeTimeFormat}`,
      ),
    ),
    feConfig.data?.reviewReportConfig?.reportPolicyLink || REPORT_POLICY_LINK,
    replaceRegexIntoLinkType.linkTag,
  )

  const reachedLimitReportingTitle = textMap.reachedLimitReportingBody.replace(
    "[x%]",
    `${reviewThrottle.reviewsReportedPercentage}%`,
  )

  const blockedReportingTitle = textMap.blockedReportingTitle.replace(
    "{date}",
    dayjs(reviewThrottle.reviewsReportingFreezeEndDate).format(
      `DD MMM YYYY ${rangeTimeFormat}`,
    ),
  )

  const blockedReportingBody = replaceRegexIntoLink(
    textMap.blockedReportingBody
      .replace(
        "{X%}",
        `${reviewThrottle.reviewsReportedIncorrectlyPercentage}%`,
      )
      .replace(
        "[date 1]",
        dayjs(reviewThrottle.reviewsEvaluationStartDate).format("DD MMM YYYY"),
      )
      .replace(
        "[date 2]",
        dayjs(reviewThrottle.reviewsEvaluationEndDate).format("DD MMM YYYY"),
      ),
    feConfig.data?.reviewReportConfig?.reportPolicyLink || REPORT_POLICY_LINK,
    replaceRegexIntoLinkType.linkTag,
  )

  const blockedReportingBodyFooter = textMap.blockedReportingBodyFooter.replace(
    "{date}",
    dayjs(reviewThrottle.reviewsReportingFreezeEndDate).format(
      `DD MMM YYYY ${rangeTimeFormat}`,
    ),
  )

  const disabledReportingBody = replaceRegexIntoLink(
    textMap.disabledReportingBody.replace("\n", "<br/><br/>"),
    feConfig.data?.reviewReportConfig?.reportPolicyLink || REPORT_POLICY_LINK,
    replaceRegexIntoLinkType.aTag,
  )

  switch (reviewThrottle.reviewUnreportableReason) {
    case ReviewUnReportableReason.ReviewUnreportableReasonReportLimit:
      limitReachTitle = textMap.reachedLimitReportingTitle
      limitReachBodyUpper = reachedLimitReportingTitle
      limitReachBodyFooter = reachedLimitReportingBodyDetail
      break
    case ReviewUnReportableReason.ReviewUnreportableReasonReportIncorrectLimit:
      limitReachTitle = blockedReportingTitle
      limitReachBodyUpper = blockedReportingBody
      limitReachBodyFooter = blockedReportingBodyFooter
      break
    case ReviewUnReportableReason.ReviewUnreportableReasonReportLimitExtended:
      limitReachTitle = textMap.reachedLimitReportingTitle
      limitReachBodyUpper = reachedLimitReportingTitle
      limitReachBodyMiddle = textMap.blockedReportingBodyMiddle
      limitReachBodyFooter = reachedLimitReportingBodyDetail
      break
    case ReviewUnReportableReason.ReviewUnreportableReasonReportIncorrectLimitExtended:
      limitReachTitle = blockedReportingTitle
      limitReachBodyUpper = blockedReportingBody
      limitReachBodyMiddle = textMap.blockedReportingBodyMiddle
      limitReachBodyFooter = blockedReportingBodyFooter
      break
    case ReviewUnReportableReason.ReviewUnreportableReasonReportDisabled:
      limitReachTitle = textMap.disabledReportingTitle
      limitReachBodyUpper = disabledReportingBody
      break
    default:
      limitReachTitle = textMap.reachedLimitReportingTitle
      limitReachBodyUpper = reachedLimitReportingTitle
      limitReachBodyFooter = reachedLimitReportingBodyDetail
      break
  }

  return (
    <Drawer
      destroyOnClose
      data-testid="feedback-report-drawer"
      open={visible}
      onClose={onClose}
      closeIcon
      title={textMap.report}
      width={475}
    >
      <div className={styles.bodyDrawer}>
        <WarningFilled className={styles.iconStyling} />
        <p className={styles.titleText}>{limitReachTitle}</p>
        <p
          className={styles.titleBody}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(limitReachBodyUpper, {
              ADD_ATTR: ["target"],
            }),
          }}
        />
        {limitReachBodyMiddle && (
          <p className={styles.titleBody}>{limitReachBodyMiddle}</p>
        )}
        {limitReachBodyFooter && (
          <p
            className={styles.titleBody}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(limitReachBodyFooter, {
                ADD_ATTR: ["target"],
              }),
            }}
          />
        )}
        <div css={drawerFooter}>
          <Button
            htmlType="submit"
            type="primary"
            block
            style={{ fontWeight: 500, fontSize: 16 }}
            onClick={onClose}
          >
            {textMap.buttonConfirm}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default FeedbackReportThrottle
